<!-- fork overlay: fork-owned page (conversation pipeline kanban) — no OSS counterpart. -->
<script setup>
import {
  computed,
  reactive,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { emitter } from 'shared/helpers/mitt';

import { BUS_EVENTS } from 'shared/constants/busEvents';
import ConversationApi from 'dashboard/api/inbox/conversation';
import {
  PIPELINE_STAGE_KEY,
  PIPELINE_CONVERSATION_UPDATED,
  PIPELINE_MESSAGE_CREATED,
  PIPELINE_MESSAGE_UPDATED,
  buildStageFilterQuery,
  findStageAttribute,
  CONVERSATION_CAMELIZE_OPTIONS,
} from 'dashboard/helper/pipeline';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PipelineColumn from 'dashboard/components-next/Pipeline/PipelineColumn.vue';

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { accountScopedRoute } = useAccount();

const conversationAttributes = useMapGetter(
  'attributes/getConversationAttributes'
);
const attributeUiFlags = useMapGetter('attributes/getUIFlags');
// One subscription for the whole board — every card needs the same array.
const accountLabels = useMapGetter('labels/getLabels');

const stageAttribute = computed(() =>
  findStageAttribute(conversationAttributes.value)
);
// Dedupe case-insensitively (first occurrence wins): the backend filter and
// canonicalStage both fold case, so "Lead" and "lead" would be one stage
// fetching the same conversations into two columns.
const stages = computed(() => {
  const seen = new Set();
  return (stageAttribute.value?.attributeValues ?? []).filter(value => {
    const key = String(value).toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
});

// One entry per stage: { conversations, allCount, page, isLoading }.
// Column state is deliberately local — the global conversations store models a
// single list, not N stage lists. Null prototype: stage names are user
// input, and a stage called "constructor" must read as an absent column.
const columns = reactive(Object.create(null));
const liveMessage = ref('');
// SortableJS mutates the column arrays during a drag — hold remote updates
// off until the drop lands.
const isDragging = ref(false);
// A websocket reconnect during a drag defers its board refresh to drag end.
let reconnectRefreshPending = false;
// In-flight stage writes, keyed by conversation id. Rapid successive moves of
// the same card must read the latest optimistic stage AND land on the server
// in order, or the board and the backend end up disagreeing.
const pendingWrites = new Map();
// Remote events arriving mid-drag are held (latest per conversation) and
// replayed when the drop settles. Events for a conversation with our own
// write in flight are held separately and reconciled via column refetch when
// the write settles — the payload may be a stale pre-write echo.
const heldRemoteEvents = new Map();
const heldDuringWrite = new Map();
// Cable delivery order isn't guaranteed — held slots keep the newest payload
// by server timestamp, not by arrival.
const holdNewest = (map, data) => {
  const previous = map.get(data.id);
  if (previous && (data.updated_at ?? 0) < (previous.updated_at ?? 0)) return;
  map.set(data.id, data);
};

const totalCount = computed(() =>
  Object.values(columns).reduce((sum, column) => sum + column.allCount, 0)
);

const camelize = record => useCamelCase(record, CONVERSATION_CAMELIZE_OPTIONS);

const fetchColumn = async (stage, page = 1) => {
  const column = columns[stage];
  if (!column) return;
  column.isLoading = true;
  // Overlapping fetches for one column (refresh + reconnect + trailing
  // refetch) can resolve out of order — only the newest response applies.
  const fetchToken = (column.fetchToken ?? 0) + 1;
  column.fetchToken = fetchToken;
  try {
    const { data } = await ConversationApi.filter({
      queryData: buildStageFilterQuery(stage),
      page,
    });
    if (column.fetchToken !== fetchToken) return;
    const records = camelize(data.payload);
    // Server-confirmed snapshot — the rollback target for failed writes.
    records.forEach(record => {
      record.lastConfirmedAttributes = record.customAttributes;
    });
    if (page === 1) {
      column.conversations = records;
    } else {
      const knownIds = new Set(column.conversations.map(c => c.id));
      column.conversations.push(
        ...records.filter(record => !knownIds.has(record.id))
      );
    }
    column.allCount = data.meta.all_count;
    column.page = page;
  } catch (error) {
    useAlert(t('PIPELINE.LOAD_ERROR'));
  } finally {
    // A superseded request must not clear the flag out from under the
    // newer in-flight one.
    if (column.fetchToken === fetchToken) column.isLoading = false;
  }
};

watch(
  stages,
  newStages => {
    newStages.forEach(stage => {
      // Load-bearing: the column entry is created SYNCHRONOUSLY before the
      // fetch, so a spurious watch re-fire (the attributes getter returns
      // fresh identities on any attribute mutation) cannot double-fetch.
      if (!columns[stage]) {
        columns[stage] = {
          conversations: [],
          allCount: 0,
          page: 1,
          isLoading: false,
        };
        fetchColumn(stage);
      }
    });
    Object.keys(columns).forEach(stage => {
      if (!newStages.includes(stage)) {
        clearTimeout(columns[stage].refetchTimer);
        delete columns[stage];
      }
    });
  },
  { immediate: true }
);

const REMOTE_REFETCH_COOLDOWN_MS = 5000;

// Trailing per-column refetch: coalesces event bursts instead of dropping
// them, and waits out an active drag (fetchColumn replaces the array
// SortableJS is mutating).
const scheduleColumnRefetch = stage => {
  const column = columns[stage];
  if (!column || column.refetchTimer) return;
  const elapsed = Date.now() - (column.lastRemoteRefetchAt ?? 0);
  const delay = Math.max(REMOTE_REFETCH_COOLDOWN_MS - elapsed, 500);
  column.refetchTimer = setTimeout(() => {
    column.refetchTimer = null;
    // A refetch landing mid-drag or mid-write would resurrect the moved
    // card in its old column — wait both out.
    if (isDragging.value || pendingWrites.size > 0) {
      scheduleColumnRefetch(stage);
      return;
    }
    column.lastRemoteRefetchAt = Date.now();
    fetchColumn(stage);
  }, delay);
};

const refreshBoard = () => {
  stages.value.forEach(stage => {
    // Immediate when safe; a fetch landing mid-write would apply the
    // server's pre-write snapshot and duplicate the moved card.
    if (pendingWrites.size > 0) {
      scheduleColumnRefetch(stage);
    } else {
      fetchColumn(stage);
    }
  });
};

const conversationLabel = conversation =>
  conversation.meta?.sender?.name || `#${conversation.id}`;

// The filter endpoint matches stages case-insensitively (LOWER() = LOWER()),
// so a conversation stored as "qualificado" renders in the "Qualificado"
// column — map stored values back to the configured stage before using them
// as column keys, or removals/counts hit a nonexistent column.
const canonicalStage = value => {
  if (value == null) return null;
  const needle = String(value).toLowerCase();
  return stages.value.find(stage => stage.toLowerCase() === needle) ?? value;
};

const withStage = (hash, stage) => {
  const result = { ...hash };
  if (stage === null || stage === undefined) {
    delete result[PIPELINE_STAGE_KEY];
  } else {
    result[PIPELINE_STAGE_KEY] = stage;
  }
  return result;
};

const persistStage = (conversation, toStage) => {
  // The endpoint replaces the whole custom_attributes hash. An event held
  // during an earlier write carries the FULL server hash — newer than our
  // copy (guaranteed by the freshness guard) and authoritative for sibling
  // keys INCLUDING deletions, so it replaces rather than merges.
  const heldAttributes = heldDuringWrite.get(
    conversation.id
  )?.custom_attributes;
  const attributes = withStage(
    heldAttributes ?? conversation.customAttributes ?? {},
    toStage
  );
  // Optimistic: a second move started before this write resolves reads the
  // new stage. The caller reverts on failure.
  conversation.customAttributes = attributes;
  // Held siblings are folded in AGAIN at execution time — a queued write may
  // run long after this hash was captured, and an event arriving in that gap
  // must not be overwritten stale by the replace-all endpoint.
  let sentAttributes = attributes;
  const write = () => {
    const heldNow = heldDuringWrite.get(conversation.id)?.custom_attributes;
    sentAttributes = heldNow ? withStage(heldNow, toStage) : attributes;
    return ConversationApi.updateCustomAttributes({
      conversationId: conversation.id,
      customAttributes: sentAttributes,
    });
  };
  // Chain regardless of the previous write's outcome — each write carries the
  // full intended hash, so the last one wins.
  const chained = (pendingWrites.get(conversation.id) ?? Promise.resolve())
    .then(write, write)
    .then(() => {
      conversation.lastConfirmedAttributes = sentAttributes;
      // Sync the sent hash (incl. sibling deletions) back to the local
      // copy so the NEXT write isn't rebuilt from pre-merge state — but the
      // stage key stays whatever a later optimistic move set it to.
      conversation.customAttributes = withStage(
        sentAttributes,
        conversation.customAttributes?.[PIPELINE_STAGE_KEY] ?? null
      );
    })
    .finally(() => {
      if (pendingWrites.get(conversation.id) === chained) {
        pendingWrites.delete(conversation.id);
        // A remote event held back during the write window may carry state we
        // must not lose — but it may equally be a stale pre-write echo, so
        // refetch the affected columns instead of replaying the payload.
        const held = heldDuringWrite.get(conversation.id);
        heldDuringWrite.delete(conversation.id);
        if (held) {
          const heldStage = canonicalStage(
            held?.custom_attributes?.[PIPELINE_STAGE_KEY] ?? null
          );
          if (heldStage !== null && columns[heldStage]) {
            scheduleColumnRefetch(heldStage);
          }
          if (toStage !== null && toStage !== heldStage && columns[toStage]) {
            scheduleColumnRefetch(toStage);
          }
        }
      }
    });
  pendingWrites.set(conversation.id, chained);
  return chained;
};

const removeFromColumn = (stage, conversationId) => {
  const column = columns[stage];
  if (!column) return null;
  const index = column.conversations.findIndex(c => c.id === conversationId);
  if (index === -1) return null;
  const [conversation] = column.conversations.splice(index, 1);
  column.allCount -= 1;
  return conversation;
};

const insertIntoColumn = (stage, conversation) => {
  const column = columns[stage];
  if (!column) return;
  column.conversations.unshift(conversation);
  column.allCount += 1;
};

// Shared by drag drops and the "Move to stage" menu. When `alreadyMoved` is
// true (drag), vuedraggable has mutated both column arrays; only counts and the
// API write remain.
// Failed writes roll back to the last SERVER-CONFIRMED state, not the
// previous optimistic one — after a rapid double-move where both writes
// fail, the card must land where the server last saw it.
const rollbackCard = (conversation, failedStage) => {
  const removed = removeFromColumn(failedStage, conversation.id);
  if (!removed) return; // a later move of the same card owns the state now
  const confirmed =
    conversation.lastConfirmedAttributes ?? conversation.customAttributes;
  removed.customAttributes = confirmed;
  const confirmedStage = canonicalStage(confirmed?.[PIPELINE_STAGE_KEY]);
  if (confirmedStage !== null && columns[confirmedStage]) {
    insertIntoColumn(confirmedStage, removed);
  }
};

const moveCard = async (conversation, toStage, alreadyMoved) => {
  const fromStage = canonicalStage(
    conversation.customAttributes?.[PIPELINE_STAGE_KEY]
  );
  if (fromStage === toStage) return;
  if (!alreadyMoved) {
    removeFromColumn(fromStage, conversation.id);
    insertIntoColumn(toStage, conversation);
  } else {
    if (columns[fromStage]) columns[fromStage].allCount -= 1;
    if (columns[toStage]) columns[toStage].allCount += 1;
  }
  try {
    await persistStage(conversation, toStage);
    liveMessage.value = t('PIPELINE.A11Y.MOVED', {
      conversation: conversationLabel(conversation),
      stage: toStage,
    });
    // The optimistic drop position won't match the server's last-activity
    // sort, and the source column's page seam shifted — the trailing
    // refetches reconcile both sides.
    if (columns[toStage]) scheduleColumnRefetch(toStage);
    if (fromStage !== null && columns[fromStage]) {
      scheduleColumnRefetch(fromStage);
    }
  } catch (error) {
    rollbackCard(conversation, toStage);
    useAlert(t('PIPELINE.MOVE_ERROR'));
  }
};

const onRemove = async conversation => {
  const fromStage = canonicalStage(
    conversation.customAttributes?.[PIPELINE_STAGE_KEY]
  );
  const removed = removeFromColumn(fromStage, conversation.id);
  try {
    await persistStage(conversation, null);
    liveMessage.value = t('PIPELINE.A11Y.REMOVED', {
      conversation: conversationLabel(conversation),
    });
  } catch (error) {
    if (removed) {
      const confirmed =
        removed.lastConfirmedAttributes ?? removed.customAttributes;
      removed.customAttributes = confirmed;
      const confirmedStage = canonicalStage(confirmed?.[PIPELINE_STAGE_KEY]);
      if (confirmedStage !== null && columns[confirmedStage]) {
        insertIntoColumn(confirmedStage, removed);
      }
    }
    useAlert(t('PIPELINE.MOVE_ERROR'));
    return;
  }
  // Removal shifted the source column's page seam — reconcile.
  if (fromStage !== null && columns[fromStage]) {
    scheduleColumnRefetch(fromStage);
  }
};

// The cable payload differs from the list payload in two ways the card
// components care about: it carries `messages` instead of
// `last_non_activity_message`, and it uses explicit nulls where the list
// payload omits keys (e.g. meta.assignee on unassigned conversations) — null
// defeats the card's destructure defaults and crashes its computeds.
const normalizeRemoteRecord = data => {
  const record = camelize(data);
  record.lastNonActivityMessage ??= record.messages?.[0];
  Object.keys(record.meta || {}).forEach(key => {
    if (record.meta[key] === null) delete record.meta[key];
  });
  record.lastConfirmedAttributes = record.customAttributes;
  return record;
};

// Remote stage changes (other agents, automations) arrive over the existing
// conversation ActionCable channel; the fork actionCable overlay relays them.
// Strict null checks throughout: a stage named "" or "0" is falsy but valid.
const onRemoteConversationUpdated = data => {
  const currentStage =
    Object.keys(columns).find(stage =>
      columns[stage].conversations.some(c => c.id === data.id)
    ) ?? null;

  // Mirror the Vuex mutation's freshness guard — cable delivery order is
  // not guaranteed across processes. Runs BEFORE the hold branches too: a
  // stale payload must neither move a fresher card nor be held and later
  // merged into a queued server write.
  if (currentStage !== null) {
    const loaded = columns[currentStage].conversations.find(
      c => c.id === data.id
    );
    if (loaded?.updatedAt && data.updated_at < loaded.updatedAt) return;
  }

  if (isDragging.value) {
    holdNewest(heldRemoteEvents, data);
    return;
  }
  // Hold while our own stage write is in flight — the echo of the pre-write
  // state would yank the card back; persistStage reconciles on settle.
  if (pendingWrites.has(data.id)) {
    holdNewest(heldDuringWrite, data);
    return;
  }
  const remoteStage = canonicalStage(
    data?.custom_attributes?.[PIPELINE_STAGE_KEY] ?? null
  );

  if (currentStage === remoteStage) {
    if (currentStage === null) return;
    // Same column — merge into the loaded card so preview/assignee/unread
    // stay live. Merge, don't replace (incl. nested meta/sender): the cable
    // payload omits list-only fields (e.g. sla_policy_id, sender
    // availability_status) that the card still needs.
    const list = columns[currentStage].conversations;
    const index = list.findIndex(c => c.id === data.id);
    const existing = list[index];
    const record = normalizeRemoteRecord(data);
    const merged = {
      ...existing,
      ...record,
      meta: {
        ...existing.meta,
        ...record.meta,
        sender: { ...existing.meta?.sender, ...record.meta?.sender },
      },
    };
    // An explicit null in the cable meta means CLEARED (e.g. unassigned) —
    // normalize dropped the key to protect the card's destructures, so
    // delete it from the merge too or the old assignee sticks.
    Object.keys(data.meta || {}).forEach(key => {
      if (data.meta[key] === null) delete merged.meta[key];
    });
    list.splice(index, 1, merged);
    return;
  }

  if (currentStage !== null) {
    removeFromColumn(currentStage, data.id);
    // The source column's page seam shifted — reconcile it too.
    scheduleColumnRefetch(currentStage);
    if (remoteStage !== null && columns[remoteStage]) {
      // Instant feedback first; the trailing refetch then reconciles server
      // order and the pagination seam the unshift position can't know.
      insertIntoColumn(remoteStage, normalizeRemoteRecord(data));
      scheduleColumnRefetch(remoteStage);
    }
    return;
  }

  // Card not loaded anywhere — pagination means we can't tell "new to the
  // stage" from "below the fold in the same stage", so inserting would
  // create duplicate counts/phantom top cards. Refetch the target column
  // (trailing, coalesced) for authoritative membership, order and count.
  if (remoteStage !== null && columns[remoteStage]) {
    scheduleColumnRefetch(remoteStage);
  }
};

const onDragEnd = () => {
  isDragging.value = false;
  const held = [...heldRemoteEvents.values()];
  heldRemoteEvents.clear();
  held.forEach(data => onRemoteConversationUpdated(data));
  if (reconnectRefreshPending) {
    reconnectRefreshPending = false;
    refreshBoard();
  }
};

// Plain message activity fires no conversation.updated — patch the loaded
// card in place so preview/timestamp/unread stay live. Field-level patching
// is drag-safe (no array mutation). message_type 2 = activity.
const onRemoteMessageCreated = data => {
  const conversationId = data?.conversation_id;
  if (conversationId == null) return;
  const stage = Object.keys(columns).find(s =>
    columns[s].conversations.some(c => c.id === conversationId)
  );
  if (!stage) return;
  const list = columns[stage].conversations;
  const index = list.findIndex(c => c.id === conversationId);
  const card = list[index];
  // Two rapid messages can be delivered out of order — never regress. Floor
  // both sides: last_activity_at is whole seconds while updated_at carries
  // sub-second precision, and a same-second message must NOT be dropped.
  if (
    Math.floor(data.conversation?.last_activity_at ?? 0) <
    Math.floor(card.updatedAt ?? 0)
  )
    return;
  // Message ids are monotonic — the strict recency key same-second
  // timestamps can't provide.
  if (
    data.message_type !== 2 &&
    data.id >= (card.lastNonActivityMessage?.id ?? 0)
  ) {
    card.lastNonActivityMessage = camelize(data);
  }
  card.timestamp = data.conversation?.last_activity_at ?? card.timestamp;
  card.unreadCount = data.conversation?.unread_count ?? card.unreadCount;
  // Advance the freshness marker so an older conversation.updated queued
  // before this message can't overwrite the patch.
  card.updatedAt = Math.max(
    card.updatedAt ?? 0,
    data.conversation?.last_activity_at ?? 0
  );
  // Columns are sorted by last activity — bubble the card up to match the
  // server order. Mid-drag the array can't be mutated; the trailing refetch
  // (which itself waits out the drag) reconciles instead.
  if (index > 0) {
    if (isDragging.value) {
      scheduleColumnRefetch(stage);
    } else {
      list.unshift(list.splice(index, 1)[0]);
    }
  }
};

// An edited/deleted message only matters to the board when it IS the
// rendered preview.
const onRemoteMessageUpdated = data => {
  const conversationId = data?.conversation_id;
  if (conversationId == null) return;
  const stage = Object.keys(columns).find(s =>
    columns[s].conversations.some(c => c.id === conversationId)
  );
  if (!stage) return;
  const card = columns[stage].conversations.find(c => c.id === conversationId);
  if (card.lastNonActivityMessage?.id !== data.id) return;
  // Successive edits of one message can be delivered out of order — keep
  // the newest by the message's own updated_at when both carry one.
  const current = card.lastNonActivityMessage?.updatedAt;
  if (current && data.updated_at && data.updated_at < current) return;
  card.lastNonActivityMessage = camelize(data);
  // Advance the card's freshness marker so a conversation event queued
  // before the edit can't overwrite the fresher preview. Message payloads
  // serialize updated_at as a Rails timestamp STRING — parse to epoch
  // seconds or Math.max would poison the marker with NaN.
  const editedAt =
    typeof data.updated_at === 'number'
      ? data.updated_at
      : Date.parse(data.updated_at) / 1000;
  if (Number.isFinite(editedAt)) {
    card.updatedAt = Math.max(card.updatedAt ?? 0, editedAt);
  }
};

// Events missed while the socket was down are never replayed — refetch
// (deferred to drag end if a drag is in progress).
const onWebsocketReconnect = () => {
  if (isDragging.value) {
    reconnectRefreshPending = true;
    return;
  }
  refreshBoard();
};

onMounted(() => {
  store.dispatch('attributes/get');
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  emitter.on(PIPELINE_CONVERSATION_UPDATED, onRemoteConversationUpdated);
  emitter.on(PIPELINE_MESSAGE_CREATED, onRemoteMessageCreated);
  emitter.on(PIPELINE_MESSAGE_UPDATED, onRemoteMessageUpdated);
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, onWebsocketReconnect);
});

onBeforeUnmount(() => {
  emitter.off(PIPELINE_CONVERSATION_UPDATED, onRemoteConversationUpdated);
  emitter.off(PIPELINE_MESSAGE_CREATED, onRemoteMessageCreated);
  emitter.off(PIPELINE_MESSAGE_UPDATED, onRemoteMessageUpdated);
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, onWebsocketReconnect);
  Object.values(columns).forEach(column => clearTimeout(column.refetchTimer));
});

const isResolvingSetup = computed(
  () => !stageAttribute.value && attributeUiFlags.value.isFetching
);
</script>

<template>
  <section
    class="flex flex-col flex-1 h-full min-h-0 overflow-hidden bg-n-surface-1"
  >
    <p class="sr-only" role="status" aria-live="polite">{{ liveMessage }}</p>
    <header class="flex items-center justify-between gap-4 px-6 py-4 shrink-0">
      <div class="flex items-baseline gap-3 min-w-0">
        <h1 class="text-xl font-medium truncate text-n-slate-12">
          {{ t('PIPELINE.HEADER.TITLE') }}
        </h1>
        <span
          v-if="stageAttribute"
          class="text-sm tabular-nums text-n-slate-11"
        >
          {{ t('PIPELINE.HEADER.TOTAL', { count: totalCount }, totalCount) }}
        </span>
      </div>
      <Button
        v-if="stageAttribute"
        :label="t('PIPELINE.HEADER.REFRESH')"
        icon="i-lucide-refresh-cw"
        size="sm"
        variant="ghost"
        color="slate"
        @click="refreshBoard"
      />
    </header>

    <div
      v-if="isResolvingSetup"
      class="flex items-center justify-center flex-1 text-n-slate-11"
    >
      <Spinner />
    </div>

    <section
      v-else-if="!stageAttribute"
      class="flex flex-col items-center justify-center flex-1 gap-4 px-8 text-center"
    >
      <span
        class="i-lucide-kanban size-10 text-n-slate-10"
        aria-hidden="true"
      />
      <div class="flex flex-col max-w-md gap-2">
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ t('PIPELINE.SETUP.TITLE') }}
        </h2>
        <p class="text-sm leading-6 text-n-slate-11">
          {{ t('PIPELINE.SETUP.DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-if="isAdmin"
        :label="t('PIPELINE.SETUP.ACTION')"
        icon="i-lucide-plus"
        size="sm"
        @click="$router.push(accountScopedRoute('attributes_list'))"
      />
    </section>

    <div
      v-else
      class="flex items-start flex-1 min-h-0 gap-3 px-6 pb-6 overflow-x-auto"
    >
      <PipelineColumn
        v-for="stage in stages"
        :key="stage"
        :stage="stage"
        :stages="stages"
        :conversations="columns[stage]?.conversations ?? []"
        :all-count="columns[stage]?.allCount ?? 0"
        :is-loading="columns[stage]?.isLoading ?? false"
        :account-labels="accountLabels"
        @card-dropped="e => moveCard(e.conversation, e.toStage, true)"
        @menu-move="e => moveCard(e.conversation, e.toStage, false)"
        @remove="onRemove"
        @drag-start="isDragging = true"
        @drag-end="onDragEnd"
        @load-more="fetchColumn(stage, (columns[stage]?.page ?? 1) + 1)"
      />
    </div>
  </section>
</template>
